/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import cx from 'classnames';
import NextImage from '@/components/NextImage';
import { PlayButton } from '@/components/PlayButton/PlayButton';
import { type ReviewMediaItem } from '../../../interface/types';
import { useLabels } from '@/hooks/useLabels';

const labelMap = {
  select: 'label_buyers_guide_select',
  video: 'label_video',
  image: 'label_image',
  review: 'label_single_review',
};

type ModalThumbnailsProps = {
  mediaItems: ReviewMediaItem[];
  imageIndex: number;
  setImageIndex: (newIndex: number) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
};

export const ModalThumbnails: React.FC<ModalThumbnailsProps> = ({
  mediaItems,
  imageIndex,
  setImageIndex,
  containerRef,
}) => {
  const labels = useLabels(labelMap);

  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const children = container.children;
      const selectedImage = children[imageIndex] as HTMLElement | undefined;

      if (selectedImage) {
        const containerRect = container.getBoundingClientRect();
        const selectedRect = selectedImage.getBoundingClientRect();

        const scrollLeft =
          selectedRect.left -
          containerRect.left -
          (containerRect.width / 2 - selectedRect.width / 2);

        container.scrollTo({
          left: container.scrollLeft + scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [containerRef, imageIndex]);
  return (
    <div className={styles.modalThumbnails} ref={containerRef}>
      {mediaItems.map((media, index) => {
        const thumbnailUrl = media.type === 'photo' ? media.url : media.thumbnailUrl;
        const isVideo = media.type === 'video';

        return (
          <button
            key={`media-${index}-${thumbnailUrl}`}
            onClick={() => {
              setImageIndex(index);
            }}
            className={cx({ [styles.activeImage]: index === imageIndex })}
            aria-label={`${labels.select} ${isVideo ? labels.video : labels.image} #${index + 1}`}
            aria-pressed={index === imageIndex}
          >
            <div className={styles.thumbnailWrapper}>
              <NextImage
                src={thumbnailUrl}
                alt={media.caption || `${isVideo ? labels.video : labels.image} #${index + 1}`}
                width={72}
                height={72}
                className={styles.thumbnailImages}
              />
              {isVideo && (
                <div className={styles.playButtonOverlay} aria-hidden="true">
                  <PlayButton size={3.5} />
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};
